import React, { useState, useEffect } from "react";
import { AuthService } from "../auth/authService";
import { Session } from "../auth/session";
import { RemoteLogger } from "../services/remoteLogger";
import { Header } from "./Header";
import EmailLoginFlow from "./EmailLoginFlow";
import SetPasswordStep from "./SetPasswordStep";

interface LoginViewProps {
  onSuccess: () => void;
}

const LoginView: React.FC<LoginViewProps> = ({ onSuccess }) => {
  const [loginMode, setLoginMode] = useState<"truid" | "phone">("truid");
  const [truId, setTruId] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [status, setStatus] = useState<"none" | "pending" | "approved" | "rejected">("none");
  // push = gettruID / phone approval (default); email = email + password and password reset;
  // set_password = optional step after push login for accounts without a password
  const [view, setView] = useState<"push" | "email" | "set_password">("push");

  const validateTruId = (val: string): boolean => {
    return /^[a-zA-Z0-9._]{3,}$/.test(val);
  };

  const handleInitiateLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (loginMode === "truid") {
      RemoteLogger.info("[LoginView] Initiating push login for gettruID:", { truId });
      if (!truId) {
        setError("Please enter your gettruID.");
        return;
      }
      if (!validateTruId(truId)) {
        setError("gettruID must be min 3 characters and contain only letters, numbers, dots, and underscores.");
        return;
      }

      setLoading(true);
      try {
        const result = await AuthService.initiateTruIdPushLogin(truId);
        if (result.success && result.challengeId) {
          setChallengeId(result.challengeId);
          setStatus("pending");
        } else {
          setError(result.error || "Failed to send push notification. Please check your inputs.");
        }
      } catch {
        setError("An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    } else {
      RemoteLogger.info("[LoginView] Initiating push login for:", { phoneCountryCode, phone });
      
      if (!phoneCountryCode || !phone) {
        setError("Please enter both country code and phone number.");
        return;
      }
      if (!phoneCountryCode.startsWith("+")) {
        setError("Country code must start with '+' (e.g. +91).");
        return;
      }

      setLoading(true);
      try {
        const result = await AuthService.initiatePushLogin(phoneCountryCode, phone);
        if (result.success && result.challengeId) {
          setChallengeId(result.challengeId);
          setStatus("pending");
        } else {
          setError(result.error || "Failed to send push notification. Please check your inputs.");
        }
      } catch {
        setError("An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Status Polling Effect
  useEffect(() => {
    if (!challengeId || status !== "pending") return;

    let isSubscribed = true;
    const interval = setInterval(async () => {
      try {
        const result = await AuthService.pollPushStatus(challengeId);
        if (!isSubscribed) return;

        if (result.status === "APPROVED") {
          clearInterval(interval);
          setStatus("approved");
          // Offer to add a password (skippable) if this account doesn't have one yet
          if (result.data?.user?.isPasswordSet === false) {
            setView("set_password");
          } else {
            onSuccess();
          }
        } else if (result.status === "REJECTED") {
          clearInterval(interval);
          setStatus("rejected");
          setError("Login request was rejected on your mobile device.");
          setChallengeId(null);
          setStatus("none");
        } else if (result.status === "EXPIRED") {
          clearInterval(interval);
          setError("Login request expired. Please try again.");
          setChallengeId(null);
          setStatus("none");
        }
      } catch (err) {
        RemoteLogger.warn("[LoginView] Status polling error:", err);
      }
    }, 2000);

    return () => {
      isSubscribed = false;
      clearInterval(interval);
    };
  }, [challengeId, status]);

  const handleCancel = () => {
    setChallengeId(null);
    setStatus("none");
    setError("");
    setLoading(false);
  };

  const handleSimulateAction = async (action: "APPROVE" | "REJECT") => {
    if (!challengeId) return;
    setError("");
    try {
      const res = await AuthService.simulatePushAction(challengeId, action);
      if (!res.success) {
        setError(res.error || "Simulation action failed.");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to trigger simulation.");
    }
  };

  const handleLocalApprove = async () => {
    setError("");
    try {
      const mockToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOjE4OCwidHlwZSI6IkFDQ0VTUyIsImlhdCI6MTc4MTA2Nzk2NCwiZXhwIjoxNzgxMTU0MzY0fQ.fsSWi06tkGx74spEV-X7eqFks1GSgo5ef6jvreouY60";
      await Session.setToken(mockToken);
      await Session.setUser({
        id: 161,
        username: "jacksoncheriyan05@outlook.com",
        status: "ACTIVE",
        isActive: true,
        role: "USER",
        firstName: "John",
        lastName: "Doe",
        phoneCountryCode: phoneCountryCode,
        phone: phone,
        isInPersonVerified: false
      });
      setStatus("approved");
      onSuccess();
    } catch (err) {
      setError("Failed to simulate local login.");
    }
  };

  const handleLocalReject = () => {
    setStatus("rejected");
    setError("Simulated rejection: Login request was rejected.");
    setChallengeId(null);
    setStatus("none");
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-white animate-fade-in">
      {/* Header */}
      <Header title="for Outlook" />

      {/* Login Form / Polling View */}
      <main className="flex-1 px-4 py-6 flex flex-col justify-between">
        {view === "set_password" ? (
          <SetPasswordStep onDone={onSuccess} />
        ) : view === "email" ? (
          <EmailLoginFlow
            onSuccess={onSuccess}
            onBack={() => {
              setView("push");
              setError("");
            }}
          />
        ) : status === "pending" ? (
          <div className="flex-1 flex flex-col items-center justify-center my-8 text-center animate-fade-in">
            {/* Spinning indicator */}
            <div className="relative w-16 h-16 flex items-center justify-center mb-6">
              <div className="absolute w-16 h-16 border-4 border-gray-100 rounded-full"></div>
              <div className="absolute w-16 h-16 border-4 border-t-[#33ccbf] rounded-full animate-spin"></div>
              <svg className="w-6 h-6 text-[#33ccbf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>

            <h3 className="text-sm font-bold text-gray-800 mb-2">Approve on your Mobile App</h3>
            <p className="text-[11px] text-gray-500 max-w-[240px] leading-relaxed mb-1">
              {loginMode === "truid"
                ? "We've sent a sign-in approval to the gettru linked to this gettruID:"
                : "We've sent a secure push notification to your registered device with phone number:"}
            </p>
            <p className="text-xs font-bold text-gray-700 bg-gray-50 px-3 py-1 rounded-full border border-gray-100 inline-block mb-6">
              {loginMode === "truid" ? truId : `${phoneCountryCode} ${phone}`}
            </p>

            <div className="flex items-center gap-1.5 justify-center py-2 px-3.5 bg-[#e5f0fb]/40 rounded-xl mb-8 animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-[#33ccbf]" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                Waiting for approval...
              </span>
            </div>

            <button
              onClick={handleCancel}
              className="px-6 py-2.5 text-xs font-bold text-gray-500 bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-all active:scale-[0.98]"
            >
              {loginMode === "truid" ? "Cancel & Edit gettruID" : "Cancel & Edit Phone"}
            </button>

            {/* Simulation Panel */}
            {/* <div className="mt-8 p-4 bg-gray-50 border border-dashed border-gray-300 rounded-xl w-full text-center">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-3">
                Simulator Panel (Temporary)
              </span>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSimulateAction("APPROVE")}
                    className="flex-1 py-2 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                  >
                    API Approve
                  </button>
                  <button
                    onClick={() => handleSimulateAction("REJECT")}
                    className="flex-1 py-2 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    API Reject
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleLocalApprove}
                    className="flex-1 py-2 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Mock Approve
                  </button>
                  <button
                    onClick={handleLocalReject}
                    className="flex-1 py-2 text-[10px] font-bold text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Mock Reject
                  </button>
                </div>
              </div>
            </div> */}
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-between">
            {loginMode === "truid" ? (
              <div>
                <h2 className="text-base font-bold text-gray-800 mb-1">Sign in with your gettruID</h2>
                <p className="text-xs text-gray-500 mb-5">
                  We'll send a sign-in approval to the gettru linked to this gettruID.
                </p>

                <form onSubmit={handleInitiateLogin} className="flex flex-col gap-4">
                  {/* TruID Field */}
                  <div className="flex flex-col gap-1">
                    <label htmlFor="truid-input" className="text-xs font-semibold text-gray-700">
                      gettruID
                    </label>
                    <div className="flex w-full text-sm border border-gray-300 rounded-lg focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed">
                    <span className=" px-2 text-gray-400 text-lg border-r  border-gray-300 flex justify-center items-center">@</span>
                    <input
                      id="truid-input"
                      type="text"
                      placeholder="your.gettruid"
                      value={truId}
                      onChange={(e) => setTruId(e.target.value)}
                      disabled={loading}
                      className="w-full disabled:bg-gray-100  mx-2 my-2  disabled:cursor-not-allowed"
                    />
                    </div>

                    <p className="text-[10px] text-gray-500 mt-1 leading-normal">
                      Letters, numbers, dots and underscores. Min 3 characters.
                    </p>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="text-xs text-red-700 bg-red-50 border-l-4 border-red-600 px-3 py-2 rounded-lg leading-relaxed">
                      {error}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    id="login-button"
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 mt-2 text-sm font-semibold text-white bg-[#33ccbf] rounded-lg hover:bg-[#44E3D6] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5 active:scale-[0.98]"
                  >
                    {loading ? "Sending approval..." : "Send approval"}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <span className="text-xs text-gray-500">Forgot your gettruID? </span>
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMode("phone");
                      setError("");
                    }}
                    className="text-xs font-semibold text-[#33ccbf] hover:underline focus:outline-none"
                  >
                    Sign in with phone
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setView("email");
                      setError("");
                    }}
                    className="text-xs font-semibold text-gray-600 hover:text-[#33ccbf] hover:underline focus:outline-none"
                  >
                    Sign in with email & password
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-base font-bold text-gray-800 mb-1">Sign In via Push</h2>
                <p className="text-xs text-gray-500 mb-5">
                  Enter your mobile number to receive a secure login request.
                </p>

                <form onSubmit={handleInitiateLogin} className="flex flex-col gap-4">
                  {/* Phone Number Field */}
                  <div className="flex flex-col gap-1">
                    <label htmlFor="phone-input" className="text-xs font-semibold text-gray-700">
                      Mobile Number
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="country-code-input"
                        type="text"
                        placeholder="+91"
                        value={phoneCountryCode}
                        onChange={(e) => setPhoneCountryCode(e.target.value)}
                        disabled={loading}
                        className="w-16 px-2.5 py-2 text-sm border border-gray-400 rounded-lg text-center font-bold focus:outline-none focus:border-[#33ccbf] focus:ring-1 focus:ring-[#33ccbf] disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      <input
                        id="phone-input"
                        type="tel"
                        placeholder="98000100"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={loading}
                        className="flex-1 px-3 py-2 text-sm border border-gray-400 rounded-lg focus:outline-none focus:border-[#33ccbf] focus:ring-1 focus:ring-[#33ccbf] disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="text-xs text-red-700 bg-red-50 border-l-4 border-red-600 px-3 py-2 rounded-lg leading-relaxed">
                      {error}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    id="login-button"
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 mt-2 text-sm font-semibold text-white bg-[#33ccbf] rounded-lg hover:bg-[#44E3D6] disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5 active:scale-[0.98]"
                  >
                    {loading ? "Sending notification..." : "Send Login Request"}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMode("truid");
                      setError("");
                    }}
                    className="text-xs font-semibold text-[#33ccbf] hover:underline focus:outline-none"
                  >
                    Back
                  </button>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setView("email");
                      setError("");
                    }}
                    className="text-xs font-semibold text-gray-600 hover:text-[#33ccbf] hover:underline focus:outline-none"
                  >
                    Sign in with email & password
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default LoginView;
